import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Lock, MapPin, Calendar, Tag, Building } from "lucide-react";
import SiteShell from "../site/SiteShell";
import PageHero from "../site/PageHero";
import { Reveal } from "../site/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  upcoming: "Upcoming",
  expressions_of_interest: "Expressions of Interest",
  tendering: "Tendering",
  awarded: "Awarded",
  closed: "Closed",
};

export default function Opportunities() {
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/opportunities`).then((r) => {
      setOpps(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <SiteShell>
      <PageHero
        overline="Current Opportunities"
        title="Live opportunities are shared with suitable Delivery Partners."
        kicker="Verified Workforce works with construction clients across planned maintenance, refurbishment and specialist work packages. Opportunities are matched by trade, location, capacity, compliance status and project requirements."
        image="https://images.unsplash.com/photo-1577495508048-b635879837f1?auto=format&fit=crop&w=1800&q=70"
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/delivery-partners#apply" className="vw-btn vw-btn--primary" data-testid="opps-join-cta">
            Join the Delivery Network
            <ArrowUpRight size={16} />
          </Link>
          <Link to="/contact" className="vw-btn vw-btn--ghost">
            Discuss a Package
          </Link>
        </div>
      </PageHero>

      <section className="vw-section bg-white" data-testid="opportunities-section">
        <div className="vw-container">
          {loading ? (
            <div className="text-center py-20 text-[var(--vw-muted)]">Loading opportunities…</div>
          ) : opps.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {opps.map((o) => (
                <OpportunityCard key={o.id} opp={o} />
              ))}
            </div>
          )}
        </div>
      </section>
    </SiteShell>
  );
}

function EmptyState() {
  return (
    <Reveal>
      <div className="relative overflow-hidden grid lg:grid-cols-12 gap-10 items-center p-10 lg:p-16 bg-[var(--vw-paper)] border border-[var(--vw-line)]" data-testid="opps-empty-state">
        <div className="lg:col-span-7">
          <div className="vw-overline mb-4">Opportunities</div>
          <h2 className="font-display text-3xl lg:text-5xl font-semibold text-[var(--vw-navy)] tracking-tight leading-[1.05] max-w-2xl">
            Suitable opportunities are released to the network.
          </h2>
          <p className="mt-6 text-[var(--vw-muted)] leading-relaxed max-w-2xl">
            Live opportunities are matched to Delivery Partners based on trade, location,
            capacity, compliance status and project requirements. Become a Verified
            Delivery Partner to access suitable opportunities as they are released.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/delivery-partners#apply" className="vw-btn vw-btn--primary" data-testid="opps-empty-apply-cta">
              Join the Delivery Network
              <ArrowUpRight size={16} />
            </Link>
            <Link to="/readiness" className="vw-btn vw-btn--outline-dark">
              Start the Readiness Assessment
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="relative aspect-[4/3] bg-[var(--vw-navy)] text-white overflow-hidden">
            <div className="vw-grid-bg" />
            <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
              <Lock size={28} className="text-[var(--vw-gold)] mb-4" />
              <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--vw-gold)] mb-2">
                Partners Only
              </div>
              <div className="font-display text-xl text-white max-w-xs">
                Full opportunity details are released to registered Delivery Partners.
              </div>
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

function OpportunityCard({ opp }) {
  const limited = opp.partners_only;
  return (
    <div className="vw-card h-full flex flex-col" data-testid={`opp-card-${opp.id}`}>
      <div className="flex items-center justify-between">
        <span className="vw-badge vw-badge--blue">{STATUS_LABEL[opp.status] || opp.status}</span>
        {limited && <span className="vw-chip vw-chip--gold"><Lock size={12}/> Partners Only</span>}
      </div>
      <h3 className="mt-5 font-display text-xl font-semibold text-[var(--vw-navy)] leading-tight">{opp.title}</h3>
      <ul className="mt-4 space-y-2 text-sm text-[var(--vw-charcoal)]">
        {opp.sector && <li className="flex items-center gap-2"><Building size={14} className="text-[var(--vw-blue)]"/>{opp.sector}</li>}
        {opp.region && <li className="flex items-center gap-2"><MapPin size={14} className="text-[var(--vw-blue)]"/>{opp.region}</li>}
        {opp.trades_required?.length > 0 && <li className="flex items-center gap-2"><Tag size={14} className="text-[var(--vw-blue)]"/>{opp.trades_required.join(", ")}</li>}
        {opp.value_band && <li className="flex items-center gap-2 font-mono text-xs tracking-wide">{opp.value_band}</li>}
        {opp.start_date && <li className="flex items-center gap-2"><Calendar size={14} className="text-[var(--vw-blue)]"/>{opp.start_date}</li>}
      </ul>
      {opp.description && <p className="mt-4 text-sm text-[var(--vw-muted)] line-clamp-3">{opp.description}</p>}
      <div className="mt-auto pt-6 border-t border-[var(--vw-line)]">
        <Link to="/delivery-partners#apply" className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--vw-blue)]" data-testid={`opp-interest-${opp.id}`}>
          Express Interest <ArrowUpRight size={14}/>
        </Link>
      </div>
    </div>
  );
}
